import { delay, http, HttpResponse } from 'msw';
import type { RequestHandler } from 'msw';
import { BUILDING_TREE, OTHER_LAYERS_TREE, findNode } from './exportTree';
import type { ExportPerimeter, ExportRequest, ExportResult } from './types';

// ─── Communes (contours simplifiés, WGS84 — données fictives) ──────────────────

function octagon([lng, lat]: [number, number], r: number): GeoJSON.Polygon {
  const ring = Array.from({ length: 8 }, (_, i) => {
    const a = (Math.PI / 4) * i + Math.PI / 8;
    const k = 0.85 + ((i * 37) % 5) * 0.06;
    return [lng + Math.cos(a) * r * 1.45 * k, lat + Math.sin(a) * r * k];
  });
  return { type: 'Polygon', coordinates: [[...ring, ring[0]]] };
}

const city = (id: string, name: string, inseeCode: string, department: string, center: [number, number], r = 0.011): ExportPerimeter => ({
  id, name, label: `${name}, France`, inseeCode, department, center, contour: octagon(center, r),
});

export const MOCK_PERIMETERS: ExportPerimeter[] = [
  {
    id: 'sevres', name: 'Sèvres', label: 'Sèvres, France', inseeCode: '92072', department: 'Hauts-de-Seine (92)', center: [2.2105, 48.8226],
    contour: {
      type: 'Polygon',
      coordinates: [[
        [2.1935, 48.8288], [2.2012, 48.8341], [2.2108, 48.8352], [2.2216, 48.8329], [2.2298, 48.8268],
        [2.2275, 48.8176], [2.2186, 48.8112], [2.2061, 48.8096], [2.1968, 48.8139], [2.1918, 48.8212], [2.1935, 48.8288],
      ]],
    },
  },
  city('saint-cloud', 'Saint-Cloud', '92064', 'Hauts-de-Seine (92)', [2.2028, 48.8443]),
  city('boulogne', 'Boulogne-Billancourt', '92012', 'Hauts-de-Seine (92)', [2.2399, 48.8353], 0.013),
  city('meudon', 'Meudon', '92048', 'Hauts-de-Seine (92)', [2.2389, 48.8121], 0.014),
  city('chaville', 'Chaville', '92022', 'Hauts-de-Seine (92)', [2.1886, 48.8082], 0.009),
  city('ville-d-avray', "Ville-d'Avray", '92077', 'Hauts-de-Seine (92)', [2.1839, 48.8261], 0.008),
  city('saint-denis', 'Saint-Denis', '93066', 'Seine-Saint-Denis (93)', [2.3574, 48.9362], 0.016),
  city('lyon-2', 'Lyon 2e Arrondissement', '69382', 'Rhône (69)', [4.8264, 45.7485], 0.012),
  city('villeurbanne', 'Villeurbanne', '69266', 'Rhône (69)', [4.8796, 45.7719], 0.016),
  city('grenoble', 'Grenoble', '38185', 'Isère (38)', [5.7245, 45.1885], 0.018),
];

const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// ─── Handlers ─────────────────────────────────────────────────────────────────

export const handlers: RequestHandler[] = [
  http.get('/api/urban-map/export/perimeters', async ({ request }) => {
    const q = normalize(new URL(request.url).searchParams.get('q') ?? '').trim();
    await delay(200);
    return HttpResponse.json(MOCK_PERIMETERS.filter((p) => normalize(p.name).includes(q) || p.inseeCode.startsWith(q)).slice(0, 6));
  }),

  http.post('/api/urban-map/export-data', async ({ request }) => {
    const body = (await request.json()) as ExportRequest;
    await delay(700);
    if (!body.perimeterId) {
      return HttpResponse.json({ message: 'Aucun périmètre sélectionné.' }, { status: 400 });
    }
    const layers = body.format === 'geojson' ? body.layers : [];
    const layerLabels = ['Bâtiments', ...layers.map((id) => findNode([OTHER_LAYERS_TREE], id)?.label ?? id)];
    const stamp = new Date().toISOString();
    const result: ExportResult = {
      exportId: `EXP-${stamp.slice(0, 10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
      fileName: `urbanmap_${normalize(body.perimeterLabel.split(',')[0]).replace(/[^a-z0-9]+/g, '-')}_${stamp.slice(0, 10)}.${body.format === 'geojson' ? 'zip' : 'xlsx'}`,
      format: body.format,
      perimeterLabel: body.perimeterLabel,
      layerCount: layerLabels.length,
      layerLabels,
      status: 'queued',
      createdAt: stamp,
    };
    // Attributs bâtiments ignorés côté mock : on vérifie juste qu'ils existent.
    body.buildingData.forEach((id) => findNode(BUILDING_TREE, id));
    return HttpResponse.json(result, { status: 202 });
  }),
];
